import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';

import { useAllServicesCost } from '../hooks/useCostData';
import type { ServiceCost } from '../types';
import {
  DEFAULT_INTERVAL,
  INTERVAL_LABELS,
  INTERVAL_MS,
  RANGE_LABELS,
  RANGE_MS,
  formatUsd,
  type IntervalKey,
  type RangeKey,
} from '../time';

// Stable color per service so the chart + table agree.
const SERVICE_COLORS: Record<ServiceCost['key'], string> = {
  merch: '#6366f1',
  video: '#0ea5e9',
  interior: '#10b981',
  ads: '#f59e0b',
  genapi: '#a855f7',
};

interface BucketRow {
  label: string;
  [serviceKey: string]: string | number;
}

export function AllServicesCostPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const [intervalKey, setIntervalKey] = useState<IntervalKey>(DEFAULT_INTERVAL['7d']);
  const [reloadKey, setReloadKey] = useState(0);

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - RANGE_MS[rangeKey]);
    return { from, to };
    // reloadKey re-snapshots "now" so Refresh re-fetches the latest data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, reloadKey]);

  const { services, loading, error } = useAllServicesCost(range);

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    setIntervalKey(DEFAULT_INTERVAL[key]);
  }

  const grandTotal = services.reduce((acc, s) => acc + s.totalCost, 0);
  const totalScans = services
    .filter((s) => s.unit === 'scans')
    .reduce((acc, s) => acc + s.count, 0);
  const avgPerScan = totalScans ? grandTotal / totalScans : 0;

  // Client-side time bucketing → one stacked bar per service.
  const buckets = useMemo<BucketRow[]>(() => {
    const step = INTERVAL_MS[intervalKey];
    const end = range.to.getTime();
    const start = Math.floor(range.from.getTime() / step) * step;
    const labelFmt =
      step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';

    const map = new Map<number, Record<string, number>>();
    for (let t = start; t <= end; t += step) {
      const zero: Record<string, number> = {};
      for (const s of services) zero[s.key] = 0;
      map.set(t, zero);
    }
    for (const s of services) {
      for (const item of s.items) {
        const bucket = Math.floor(item.t / step) * step;
        const entry = map.get(bucket);
        if (!entry) continue;
        entry[s.key] = (entry[s.key] || 0) + item.cost;
      }
    }
    return Array.from(map.entries()).map(([t, v]) => ({
      label: format(new Date(t), labelFmt),
      ...v,
    }));
  }, [services, range, rangeKey, intervalKey]);

  const hasCost = grandTotal > 0;

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">All Services Cost</h1>
          <p className="text-sm text-slate-500 mt-1">
            Cost per scan across every service in the selected time frame.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => selectRange(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  rangeKey === key
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
          <select
            value={intervalKey}
            onChange={(e) => setIntervalKey(e.target.value as IntervalKey)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            title="Bucket interval"
          >
            {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
              <option key={key} value={key}>
                {INTERVAL_LABELS[key]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            title="Refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load cost data: {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard label="Total cost" value={formatUsd(grandTotal)} sub="all services, this period" />
        <StatCard label="Total scans" value={String(totalScans)} sub="completed scans (excl. gen-api calls)" />
        <StatCard label="Avg cost / scan" value={formatUsd(avgPerScan)} sub="across all scan services" />
      </div>

      {/* Cost over time (stacked by service) */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Cost over time</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{formatUsd(grandTotal)}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && services.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading cost data…
            </div>
          ) : hasCost ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: number) => formatUsd(v)} />
                <Tooltip
                  formatter={(value: number, name: string) => [formatUsd(value), name]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
                />
                {services.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={SERVICE_COLORS[s.key]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No cost in this period
            </div>
          )}
        </div>
      </div>

      {/* Per-service breakdown */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">By service</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{services.length}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">services</span>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="hidden sm:grid grid-cols-[1fr_repeat(3,minmax(0,8rem))] gap-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <span>Service</span>
            <span className="text-right">Scans / calls</span>
            <span className="text-right">Total cost</span>
            <span className="text-right">Avg / unit</span>
          </div>
          {services.map((s) => {
            const avg = s.count ? s.totalCost / s.count : 0;
            return (
              <div
                key={s.key}
                className="grid grid-cols-2 sm:grid-cols-[1fr_repeat(3,minmax(0,8rem))] gap-2 py-2.5 text-sm items-center"
              >
                <span className="font-medium text-slate-800 truncate col-span-2 sm:col-span-1 flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: SERVICE_COLORS[s.key] }}
                  />
                  {s.label}
                  {s.warning && (
                    <span className="text-[10px] text-amber-600" title={s.warning}>
                      (unavailable)
                    </span>
                  )}
                </span>
                <span className="text-right tabular-nums text-slate-700" data-label="Scans / calls">
                  <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">
                    {s.unit === 'calls' ? 'Calls' : 'Scans'}
                  </span>
                  {s.count} {s.unit === 'calls' ? 'calls' : ''}
                </span>
                <span className="text-right tabular-nums text-slate-700" data-label="Total cost">
                  <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost</span>
                  {formatUsd(s.totalCost)}
                </span>
                <span className="text-right tabular-nums text-slate-700" data-label="Avg / unit">
                  <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Avg</span>
                  {formatUsd(avg)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
